import { compose } from "infrastructure/store";
import { ECommerceOnboardingApplication } from "consts/onboarding";
import { delay } from "util/support";
import { putECommerceMediaComplianceSteps } from "features/onboarding/api";
import type { OnboardingMediaAndComplianceCheck } from "features/onboarding/types";
import { useSimpleRequest } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { ECommerce } from "ui/forms/Onboarding";
import {
    useRequestedChanges,
    useFieldsStatuses,
    useFieldsComments,
    useApplicationDetailContext,
    useStepStatus,
    useApplicationStatus
} from "ui/organizms/Onboarding/Applications/common";

export const useMediaAndComplianceCheck = () => {
    const { getTotalStepFields, getStepData, fetchStep } = useApplicationDetailContext();

    const totalStepFields = getTotalStepFields(ECommerceOnboardingApplication.MediaCompliance);
    const data = getStepData<OnboardingMediaAndComplianceCheck>(ECommerceOnboardingApplication.MediaCompliance);

    const {
        getStepStatus
    } = useStepStatus();

    const {
        updateApplicationStatus
    } = useApplicationStatus();

    const {
        withRequestedChangeFields,
        serializeRequestedChangeFields
    } = useRequestedChanges();

    const {
        withFieldsStatuses,
        serializeFieldsStatuses
    } = useFieldsStatuses();

    const {
        withFieldsComments,
        serializeFieldsComments
    } = useFieldsComments();

    const request = useSimpleRequest();

    const stepDataWithRequestedChangeFields = compose<OnboardingMediaAndComplianceCheck>(
        withRequestedChangeFields,
        withFieldsStatuses,
        withFieldsComments
    )(data);

    const formProps: ECommerce.MediaAndComplianceCheck.OnboardingMediaAndComplianceCheckForm = {
        bootstrapData: {},
        initialValues: ECommerce.MediaAndComplianceCheck.getInitialValues(stepDataWithRequestedChangeFields),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async ({
            hasAdverseMediaCoverage,
            adverseMediaCoverageInput,
            hasLegalInvestigation,
            legalInvestigationInput,
            hasCertification,
            isCompliantWithTax,
            isRegisteredWithAuthority,
            registeredWithAuthorityInput,
            ...restFormValues
        }, helpers) => {
            const response = await request(() => putECommerceMediaComplianceSteps({
                userId: data.userId,
                hasAdverseMediaCoverage: Number(hasAdverseMediaCoverage),
                adverseMediaCoverageInput,
                hasLegalInvestigation: Number(hasLegalInvestigation),
                legalInvestigationInput,
                hasCertification: Number(hasCertification),
                isCompliantWithTax: Number(isCompliantWithTax),
                isRegisteredWithAuthority: Number(isRegisteredWithAuthority),
                registeredWithAuthorityInput,
                requestedChangeFields: serializeRequestedChangeFields(restFormValues),
                fieldsStatuses: serializeFieldsStatuses(restFormValues),
                fieldsComments: serializeFieldsComments(restFormValues),
                status: getStepStatus(restFormValues, totalStepFields)
            }));

            helpers!.setTouched({});

            await delay(1000)
                .then(() => fetchStep(ECommerceOnboardingApplication.MediaCompliance))
                .then(() => delay(1000))
                .then(updateApplicationStatus);

            return FormAdapter.fromResponse(response);
        }
    };

    return {
        ...formProps,
        ...ECommerce.MediaAndComplianceCheck.useForm(formProps),
        ...stepDataWithRequestedChangeFields
    };
};

export default useMediaAndComplianceCheck;
